import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import { createLogger } from "../../services/Logger";

type StatusState = {
  type: "success" | "error";
  message: string;
};

export const SuperAdminOptInScreen = () => {
  const api = useContext(ZeosApiContext);
  const log = useMemo(() => createLogger("SuperAdminOptIn"), []);
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<StatusState | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!token) {
      setStatus({ type: "error", message: "Missing invite token." });
      return;
    }
    if (!password || password.length < 8) {
      setStatus({ type: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (password !== confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      const response = await api.Admin.superAdminOptInAsync({
        inviteToken: token,
        password,
      });
      if (response.zeosStatus !== "success") {
        throw new Error(response.message || "Unable to activate super admin.");
      }
      setStatus({ type: "success", message: "Super admin activated. You can now sign in." });
    } catch (error: any) {
      log.error("Super admin opt-in failed", error);
      setStatus({ type: "error", message: error?.message || "Activation failed." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zeos-white-md flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Super Admin Activation</h1>
        <p className="text-sm text-gray-600 mt-1">
          Set a password to activate your super administrator account.
        </p>
        {status && (
          <div
            className={`mt-3 text-sm ${
              status.type === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {status.message}
          </div>
        )}
        <div className="mt-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          className="mt-4 bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm w-full"
          disabled={loading}
        >
          {loading ? "Activating..." : "Activate Account"}
        </button>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mt-3 text-xs text-gray-600 hover:underline w-full"
        >
          Back to login
        </button>
      </div>
    </div>
  );
};
